import { ArrowDown, ArrowUp } from "lucide-react";
import { useStripeFinances } from "@/features/stripe/hooks/stripeQueries";
import type { StripeFinanceSummary, StripeFinanceTotals } from "@/features/stripe/types/stripeTypes";
import { formatStripeMinorAmount, getStripePercentChange, isStripeFinanceAvailable } from "@/features/stripe/utils/stripeMoney";
import { Card } from "@/shared/components/ui/Card";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { Section } from "@/shared/components/ui/Section";
import { SectionAction } from "@/shared/components/ui/SectionAction";
import { StatusBadge } from "@/shared/components/ui/StatusBadge";
import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import { formatBusinessDate } from "@/shared/utils/formatters";

interface Props {
  businessId: string;
  userId: string;
  isOwner: boolean;
}

function NetChange({ totals, summary }: { totals: StripeFinanceTotals; summary: StripeFinanceSummary }) {
  const previous = summary.previousPeriod;
  const baseline = previous?.totals.find((total) => total.currency === totals.currency);
  const change = getStripePercentChange(totals.netMinor, baseline?.netMinor);
  if (!previous || change === null) return null;
  const month = formatBusinessDate(previous.start, summary.period.timezone, { month: "long" });
  const Icon = change > 0 ? ArrowUp : ArrowDown;

  return (
    <p className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
      <StatusBadge tone={change > 0 ? "positive" : change < 0 ? "danger" : "neutral"} className="gap-1 tabular-nums">
        {change !== 0 && <Icon aria-hidden="true" className="size-3" />}
        {change === 0 ? "No change" : <><span className="sr-only">{change > 0 ? "Up" : "Down"} </span>{Math.abs(change)}%</>}
      </StatusBadge>
      vs same days in {month}
    </p>
  );
}

function FinanceInsights({ totals, summary }: { totals: StripeFinanceTotals; summary: StripeFinanceSummary }) {
  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs text-muted-foreground">Net income</p>
        <p className="text-3xl font-extrabold leading-tight tabular-nums [overflow-wrap:anywhere]">
          {formatStripeMinorAmount(totals.netMinor, totals.currency)}
        </p>
        <NetChange totals={totals} summary={summary} />
      </div>
      <dl className="flex gap-6">
        <div className="min-w-0">
          <dt className="text-xs text-muted-foreground">Gross</dt>
          <dd className="font-bold tabular-nums [overflow-wrap:anywhere]">{formatStripeMinorAmount(totals.grossMinor, totals.currency)}</dd>
        </div>
        <div className="min-w-0">
          <dt className="text-xs text-muted-foreground">Payments</dt>
          <dd className="font-bold tabular-nums">{totals.paymentCount.toLocaleString("en-US")}</dd>
        </div>
      </dl>
    </div>
  );
}

export function DashboardFinanceSummary({ businessId, userId, isOwner }: Props) {
  const finances = useStripeFinances(businessId, userId, isOwner);
  const { goToFinances } = useAppNavigation();
  // Stays hidden unless Stripe is connected, so businesses without Stripe see no extra noise on Home.
  if (!isOwner || !finances.data || !isStripeFinanceAvailable(finances.data.status)) return null;
  const [primary] = finances.data.totals;

  return (
    <Section
      title="This month"
      count="From Stripe"
      action={<SectionAction aria-label="View finances" onClick={goToFinances}>View</SectionAction>}
    >
      <Card>
        {primary
          ? <FinanceInsights totals={primary} summary={finances.data} />
          : <EmptyState message="No Stripe payments or payouts this month yet." />}
      </Card>
    </Section>
  );
}
